import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { DateTime } from 'luxon'
import {
  projectedFinalScore,
  type ScoreComponent,
  type ScoreComponentKey,
} from './scoreComponents'
import type { GradeAppealDto } from './useGradeAppeal'

// The appeal's status decides the whole panel: pending is "under review",
// accepted/partially accepted/declined are the outcome the student is told about.
const TITLE = {
  pending: 'resolved_title_pending',
  accepted: 'resolved_title_accepted',
  'partially-accepted': 'resolved_title_partially_accepted',
  declined: 'resolved_title_declined',
} as const

const DESCRIPTION = {
  pending: 'resolved_description_pending',
  accepted: 'resolved_description_accepted',
  'partially-accepted': 'resolved_description_partially_accepted',
  declined: 'resolved_description_declined',
} as const

export const GradeAppealStatusPanel = observer(function GradeAppealStatusPanel({
  appeal,
  components,
  overrideFinalPercent,
}: {
  appeal: GradeAppealDto
  components: Array<ScoreComponent>
  overrideFinalPercent: number | null
}) {
  const { tt } = useTranslationTyped()
  const strings = tt.meeting.results.appeal
  const proposedByKey: Partial<Record<ScoreComponentKey, number>> = {}
  for (const component of appeal.components) {
    proposedByKey[component.component] = component.proposedScore
  }

  const currentTotalScore = projectedFinalScore(components)
  const outcomeTotalScore =
    appeal.status === 'pending'
      ? projectedFinalScore(components, proposedByKey)
      : appeal.status === 'declined'
        ? currentTotalScore
        : overrideFinalPercent
  const isOverridden =
    appeal.status === 'accepted' || appeal.status === 'partially-accepted'
  const status: `${GradeAppealDto['status']}` = appeal.status
  let outcomeText: string
  switch (status) {
    case 'pending':
      outcomeText = strings.proposed_grade({ score: outcomeTotalScore })
      break
    case 'accepted':
    case 'partially-accepted':
      outcomeText =
        outcomeTotalScore === null
          ? ''
          : tt.instructor_appeals.grade_changes({
              current: currentTotalScore,
              outcome: outcomeTotalScore,
            })
      break
    case 'declined':
      outcomeText = tt.instructor_appeals.grade_unchanged({
        score: outcomeTotalScore,
      })
      break
  }

  const submittedAt = appeal.submittedAt
    ? DateTime.fromISO(appeal.submittedAt).toLocaleString(DateTime.DATE_MED)
    : ''

  return (
    <div
      data-testid="grade-appeal-status-panel"
      data-status={appeal.status}
      className="w-full rounded-2xl border border-outline-light bg-surface-dim p-4"
    >
      <p className="text-label-medium text-on-surface">
        {strings[TITLE[appeal.status]]()}
      </p>
      <p className="mt-1 text-body-small text-on-surface-var">
        {strings[DESCRIPTION[appeal.status]]({ submittedAt })}
      </p>
      {isOverridden ? (
        <p className="mt-1 text-body-small text-on-surface-var">
          {strings.overridden_explanation()}
        </p>
      ) : null}
      {outcomeText ? (
        <p
          data-testid="grade-appeal-status-grade"
          className="mt-3 text-body-small text-on-surface"
        >
          {outcomeText}
        </p>
      ) : null}
    </div>
  )
})
